import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Box,
    Typography,
    Button,
    Grid,
    Container,
    Card,
    Stack,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material'
import { motion } from 'framer-motion'
import PaymentsIcon from '@mui/icons-material/Payments'
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'
import PublicIcon from '@mui/icons-material/Public'
import PlaceIcon from '@mui/icons-material/Place'
import SearchIcon from '@mui/icons-material/Search'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import GroupsIcon from '@mui/icons-material/Groups'
import TravelExploreIcon from '@mui/icons-material/TravelExplore'
import StorefrontIcon from '@mui/icons-material/Storefront'
import {
    diasporaCountries,
    operatingCountries,
} from '../utils/businessDirectory'
import { useLanguage } from '../contexts/LanguageContext'
import PromotionCarousel from '../components/promotions/PromotionCarousel'
import api from '../services/api'

const currencyByCountry = {
    US: 'USD',
    CA: 'CAD',
    GB: 'GBP',
    UK: 'GBP',
    AU: 'AUD',
    FR: 'EUR',
    SL: 'SLE',
    LR: 'LRD',
    GN: 'GNF',
    GM: 'GMD',
}

const fallbackWeeklyMerchants = [
    { name: 'REOVUE', initials: 'RV' },
    { name: 'MojoPayr', initials: 'MJ' },
    { name: 'Medtela', initials: 'MD' },
    { name: 'EzWaka', initials: 'EZ' },
    { name: 'Salone Market', initials: 'SM' },
]

const initialsFor = (name = '') => {
    const words = String(name)
        .replace(/[^A-Za-z0-9\s]/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)

    const initials = words.map((word) => word.charAt(0).toUpperCase()).slice(0, 2).join('')
    return initials || 'MP'
}

const Home = () => {
    const navigate = useNavigate()
    const { lang } = useLanguage()
    const [accountType, setAccountType] = useState('USER')
    const [residency, setResidency] = useState('DIASPORA')
    const [residentCountry, setResidentCountry] = useState('US')
    const [weeklyMerchants, setWeeklyMerchants] = useState([])

    const isFr = lang === 'fr'
    const residentCurrency = currencyByCountry[residentCountry] || 'USD'

    const copy = {
        chip: isFr ? 'Diaspora et Afrique dans un seul parcours' : 'Diaspora and Africa in one flow',
        heroLine1: isFr ? 'Une application de paiement africaine' : 'One Platform to Send, Pay, and',
        heroLine2: isFr ? 'pour envoyer, payer et soutenir.' : 'Connect Africa.',
        heroBody: isFr
            ? 'MojoPayr sur le web garde le meme niveau de serieux que l application : les utilisateurs diaspora recherchent des commerces par pays de destination, tandis que les utilisateurs locaux restent dans leur propre marche et recherchent directement sans friction inutile.'
            : 'The MojoPayr digital wallet helps diaspora users make cross-border payments and find businesses by destination country, while local users pay directly in their own market with no extra friction.',
        openBusinessTools: isFr ? 'Ouvrir les outils entreprise' : 'Open Business Tools',
        searchBusinesses: isFr ? 'Rechercher des commerces' : 'Search Businesses',
        createAccount: isFr ? 'Creer un compte' : 'Create Account',
        startRightProfile: isFr ? 'Commencez avec le bon profil' : 'Start With The Right Profile',
        chooseUserOrBusiness: isFr ? 'Choisissez utilisateur ou entreprise' : 'Choose user or business',
        user: isFr ? 'Utilisateur' : 'User',
        business: isFr ? 'Entreprise' : 'Business',
        chooseResidency: isFr ? 'Choisissez la residence' : 'Choose residency',
        residentCountry: isFr ? 'Pays de residence' : 'Resident country',
        residentMarket: isFr ? 'Marche de residence' : 'Resident market',
        diasporaGuideTitle: isFr ? 'Le choix du pays se fait dans le parcours de recherche paiement' : 'Country selection happens in the payment search flow',
        localGuideTitle: isFr ? 'Les utilisateurs locaux recherchent directement les commerces' : 'Local users search businesses directly',
        diasporaGuideBody: isFr ? 'Quand vous ouvrez la recherche, MojoPayr demandera dans quel pays vous payez avant d afficher les commerces.' : 'When you open search, MojoPayr will ask which country you are paying into before showing businesses.',
        localGuideBody: isFr ? 'Comme les utilisateurs locaux sont deja dans le marche, le site saute cette etape et les amene directement a la recherche de commerces.' : 'Because local users are already in-market, the website skips the extra country step and takes them straight to business search.',
        sectionTitle: isFr ? 'Experience web reconstruite pour un usage reel' : 'Website Experience, Rebuilt For Real Use',
        bottomTitle: isFr ? 'Concu pour sembler serieux, rapide, et utile' : 'Built to feel serious, fast, and useful',
        bottomBody: isFr
            ? 'Le site guide les utilisateurs diaspora vers la recherche transfrontaliere de commerces et permet aux utilisateurs locaux d aller directement au marchand voulu avec moins de clics et moins de consommation de donnees.'
            : 'The website guides diaspora users into cross-border business search and lets local users get straight to the merchant they want with fewer clicks and less data waste.',
        weeklyTitle: isFr ? 'Meilleures entreprises de la semaine' : 'Top Businesses of the Week',
        weeklyBadge: isFr ? 'En direct' : 'Live',
        weeklyBody: isFr
            ? 'Mis a jour avec les commerces actifs et les paiements factures confirmes.'
            : 'Updated from active merchants and confirmed invoice payments.',
    }

    useEffect(() => {
        let cancelled = false

        api.get('/merchants/top-weekly', { params: { limit: 5 } })
            .then((response) => {
                if (cancelled) return
                const rows = Array.isArray(response.data?.data)
                    ? response.data.data
                    : Array.isArray(response.data)
                        ? response.data
                        : []

                const merchants = rows
                    .map((item) => {
                        const name = String(item?.name || item?.business_name || '').trim()
                        if (!name) return null
                        return {
                            name,
                            initials: String(item?.initials || '').trim() || initialsFor(name),
                        }
                    })
                    .filter(Boolean)
                    .slice(0, 5)

                if (merchants.length > 0) {
                    setWeeklyMerchants(merchants)
                }
            })
            .catch(() => {
                if (!cancelled) setWeeklyMerchants([])
            })

        return () => {
            cancelled = true
        }
    }, [])

    const residentOptions = useMemo(
        () => (residency === 'DIASPORA' ? diasporaCountries : operatingCountries),
        [residency]
    )

    const summaryLines =
        accountType === 'BUSINESS'
            ? [
                isFr ? 'Acceptez les paiements des clients locaux et des familles de la diaspora.' : 'Accept payments from local customers and diaspora families.',
                isFr ? 'Creez des factures, paiements QR, recus fiscaux, et parcours de ticketing.' : 'Create invoices, QR payments, tax receipts, and ticketing flows.',
                isFr ? 'Gerez une operation marchande plus legere et plus claire depuis un seul endroit.' : 'Run a lighter, clearer merchant operation from one place.',
            ]
            : [
                residency === 'DIASPORA'
                    ? (isFr ? 'Recherchez des commerces dans les marches africains desservis et payez depuis votre devise de residence.' : 'Search businesses in African operating markets and pay from your resident currency.')
                    : (isFr ? 'Recherchez directement des commerces dans votre marche local sans choix supplementaire de pays.' : 'Search businesses in your home market directly with no extra country selection.'),
                isFr ? 'Le site garde la meme logique MojoPayr que l application.' : 'The website keeps the same MojoPayr logic as the app.',
                isFr ? 'Utile pour frais scolaires, services publics, commerce, sante, et soutien familial.' : 'Useful for school fees, utilities, retail, healthcare, and family support.',
            ]

    const featureCards = [
        {
            icon: <SearchIcon sx={{ color: '#FF8A00' }} />,
            title: isFr ? 'Recherche de commerces' : 'Business Search',
            text: isFr
                ? 'Les utilisateurs diaspora choisissent le pays dans lequel ils paient. Les utilisateurs locaux recherchent les commerces de leur marche immediatement.'
                : 'Diaspora users choose the country they are paying into. Local users search businesses in their own market immediately.',
        },
        {
            icon: <ReceiptLongIcon sx={{ color: '#FF8A00' }} />,
            title: isFr ? 'Logique de paiement reelle' : 'Real Payment Logic',
            text: isFr
                ? 'Les paiements diaspora utilisent le change et les frais comme la remittance. Les paiements locaux restent plus legers et favorables au volume.'
                : 'Diaspora payments use FX and fees like remittance. Local payments stay lighter and more volume-friendly.',
        },
        {
            icon: <QrCode2Icon sx={{ color: '#FF8A00' }} />,
            title: isFr ? 'Pret pour les marchands' : 'Merchant Ready',
            text: isFr
                ? 'Les entreprises peuvent encaisser par QR, facture, ticketing, et services marchands sans que le site paraisse basique.'
                : 'Businesses can collect by QR, invoice, ticketing, and business services without the website feeling basic.',
        },
        {
            icon: <GroupsIcon sx={{ color: '#FF8A00' }} />,
            title: isFr ? 'Afrique d abord' : 'Africa First',
            text: isFr
                ? 'Concu pour la Sierra Leone, le Liberia, la Guinee, la Gambie, et pret a s etendre sans encombrer l experience.'
                : 'Built for Sierra Leone, Liberia, Guinea, Gambia, and ready to expand without cluttering the experience.',
        },
    ]

    const bottomCards = [
        isFr ? 'La diaspora commence par le pays de residence, puis choisit le marche africain dans lequel elle paie.' : 'Diaspora starts with resident country, then chooses the African market to pay into.',
        isFr ? 'Les utilisateurs locaux sautent le selecteur de pays et recherchent directement les commerces de leur marche.' : 'Local users skip the country selector and search businesses directly in their market.',
        isFr ? 'Les proprietaires d entreprises gardent aussi un acces plus propre aux outils marchands et encaissements.' : 'Business owners still have a cleaner route into merchant tools and collections.',
    ]

    const merchantsToShow = weeklyMerchants.length > 0 ? weeklyMerchants : fallbackWeeklyMerchants

    const primaryAction = () => {
        if (accountType === 'BUSINESS') {
            window.sessionStorage.setItem(
                'mojopayr-web-entry',
                JSON.stringify({
                    accountType,
                    residency,
                    residentCountry,
                    targetCountry: '',
                })
            )
            navigate('/login')
            return
        }

        const searchEntry = {
            accountType: 'BUSINESS',
            residency,
            residentCountry,
            targetCountry: residency === 'LOCAL' ? residentCountry : '',
        }

        window.sessionStorage.setItem(
            'mojopayr-web-entry',
            JSON.stringify(searchEntry)
        )

        navigate('/payment', {
            state: searchEntry,
        })
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: `
                    radial-gradient(circle at 20% 20%, rgba(91,46,255,0.24), transparent 32%),
                    radial-gradient(circle at 85% 18%, rgba(255,138,0,0.16), transparent 28%),
                    radial-gradient(circle at 70% 75%, rgba(43,19,125,0.5), transparent 36%),
                    #090614
                `,
                color: '#FFFFFF',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Chip
                                icon={<PublicIcon />}
                                label={copy.chip}
                                sx={{
                                    mb: 3,
                                    background: 'rgba(255,138,0,0.12)',
                                    color: '#FFD6A1',
                                    border: '1px solid rgba(255,138,0,0.18)',
                                }}
                            />

                            <Typography
                                component="h1"
                                sx={{
                                    fontSize: { xs: 38, md: 64 },
                                    fontWeight: 900,
                                    lineHeight: 1.04,
                                    mb: 3,
                                    letterSpacing: '-0.03em',
                                }}
                            >
                                {copy.heroLine1}
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'block',
                                        background: 'linear-gradient(90deg,#FF8A00,#FFB347)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    {copy.heroLine2}
                                </Box>
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 16,
                                    color: '#B7B2D9',
                                    mb: 4,
                                    maxWidth: 540,
                                    lineHeight: 1.75,
                                }}
                            >
                                {copy.heroBody}
                            </Typography>

                            <Stack spacing={1.5} mb={4}>
                                {summaryLines.map((line) => (
                                    <Stack key={line} direction="row" spacing={1.2} alignItems="flex-start">
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                background: 'linear-gradient(90deg,#FF8A00,#FFB347)',
                                                mt: '8px',
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography color="#D6D0F5">{line}</Typography>
                                    </Stack>
                                ))}
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
                                <Button
                                    onClick={primaryAction}
                                    sx={{
                                        background: 'linear-gradient(90deg,#FF8A00,#FFB347)',
                                        color: '#0B0620',
                                        borderRadius: 999,
                                        px: 5,
                                        py: 1.6,
                                        fontWeight: 800,
                                        textTransform: 'none',
                                    }}
                                >
                                    {accountType === 'BUSINESS' ? copy.openBusinessTools : copy.searchBusinesses}
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/register')}
                                    sx={{
                                        borderColor: '#FF8A00',
                                        color: '#FFFFFF',
                                        borderRadius: 999,
                                        px: 5,
                                        py: 1.6,
                                        fontWeight: 700,
                                        textTransform: 'none',
                                    }}
                                >
                                    {copy.createAccount}
                                </Button>
                            </Stack>

                            <PromotionCarousel
                                platform="website"
                                country={residentCountry}
                                currency={residentCurrency}
                                userType={residency}
                                fallbackTitle={isFr ? 'MojoPayr aujourd hui' : 'MojoPayr today'}
                                fallbackMessage={isFr
                                    ? 'Ajoutez des fonds, envoyez, payez et soutenez le pays depuis un seul wallet.'
                                    : 'Top up, send, pay,  home from one MojoPayr wallet.'}
                                sx={{ mt: 3, maxWidth: 560 }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7 }}
                            >
                                <Card
                                    sx={{
                                        p: { xs: 3, md: 4 },
                                        borderRadius: 8,
                                        background: 'rgba(18,10,45,0.9)',
                                        backdropFilter: 'blur(14px)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 900, fontSize: 24, mb: 3 }}>
                                        {copy.startRightProfile}
                                    </Typography>

                                    <Typography sx={{ color: '#B7B2D9', mb: 1.5 }}>
                                        {copy.chooseUserOrBusiness}
                                    </Typography>
                                    <ToggleButtonGroup
                                        fullWidth
                                        exclusive
                                        value={accountType}
                                        onChange={(_, value) => value && setAccountType(value)}
                                        sx={{
                                            mb: 3,
                                            '& .MuiToggleButton-root': {
                                                color: '#C9C6E3',
                                                borderColor: 'rgba(255,255,255,0.08)',
                                                textTransform: 'none',
                                                py: 1.4,
                                            },
                                            '& .Mui-selected': {
                                                background:
                                                    'linear-gradient(90deg,#FF8A00,#FFB347) !important',
                                            },
                                            '& .Mui-selected, & .Mui-selected *': {
                                                color: '#0B0620 !important',
                                            },
                                        }}
                                    >
                                        <ToggleButton
                                            value="USER"
                                            sx={{
                                                color: accountType === 'USER' ? '#000000 !important' : '#FFFFFF',
                                                fontWeight: 800,
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <PaymentsIcon fontSize="small" />
                                                <span>{copy.user}</span>
                                            </Stack>
                                        </ToggleButton>
                                        <ToggleButton
                                            value="BUSINESS"
                                            sx={{
                                                color: accountType === 'BUSINESS' ? '#000000 !important' : '#FFFFFF',
                                                fontWeight: 800,
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <BusinessCenterIcon fontSize="small" />
                                                <span>{copy.business}</span>
                                            </Stack>
                                        </ToggleButton>
                                    </ToggleButtonGroup>

                                    <Typography sx={{ color: '#B7B2D9', mb: 1.5 }}>
                                        {copy.chooseResidency}
                                    </Typography>
                                    <ToggleButtonGroup
                                        fullWidth
                                        exclusive
                                        value={residency}
                                        onChange={(_, value) => value && setResidency(value)}
                                        sx={{
                                            mb: 3,
                                            '& .MuiToggleButton-root': {
                                                color: '#C9C6E3',
                                                borderColor: 'rgba(255,255,255,0.08)',
                                                textTransform: 'none',
                                                py: 1.4,
                                            },
                                            '& .Mui-selected': {
                                                background:
                                                    'linear-gradient(90deg,#5B2EFF,#7D52FF) !important',
                                            },
                                            '& .Mui-selected, & .Mui-selected *': {
                                                color: '#FFFFFF !important',
                                            },
                                        }}
                                    >
                                        <ToggleButton
                                            value="DIASPORA"
                                            sx={{
                                                color: residency === 'DIASPORA' ? '#FFFFFF !important' : '#FFFFFF',
                                                fontWeight: 800,
                                            }}
                                        >
                                            Diaspora
                                        </ToggleButton>
                                        <ToggleButton
                                            value="LOCAL"
                                            sx={{
                                                color: residency === 'LOCAL' ? '#FFFFFF !important' : '#FFFFFF',
                                                fontWeight: 800,
                                            }}
                                        >
                                            Local
                                        </ToggleButton>
                                    </ToggleButtonGroup>

                                    <Typography sx={{ color: '#B7B2D9', mb: 1.5 }}>
                                        {residency === 'DIASPORA' ? copy.residentCountry : copy.residentMarket}
                                    </Typography>
                                    <Box
                                        component="select"
                                        value={residentCountry}
                                        onChange={(e) => setResidentCountry(e.target.value)}
                                        sx={{
                                            width: '100%',
                                            border: '1px solid rgba(255,255,255,0.08)',
                                            background: 'rgba(255,255,255,0.03)',
                                            color: '#FFFFFF',
                                            borderRadius: 4,
                                            px: 2,
                                            py: 1.75,
                                            outline: 'none',
                                            fontSize: 15,
                                            mb: 3,
                                        }}
                                    >
                                        {residentOptions.map((item) => (
                                            <option
                                                key={item.code}
                                                value={item.code}
                                                style={{ color: '#111827' }}
                                            >
                                                {item.label}
                                            </option>
                                        ))}
                                    </Box>

                                    <Box
                                        sx={{
                                            p: 2.2,
                                            borderRadius: 4,
                                            background: 'rgba(255,255,255,0.03)',
                                            border: '1px solid rgba(255,255,255,0.06)',
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                            {residency === 'DIASPORA' ? (
                                                <TravelExploreIcon sx={{ color: '#FF8A00', mt: 0.3 }} />
                                            ) : (
                                                <PlaceIcon sx={{ color: '#FF8A00', mt: 0.3 }} />
                                            )}
                                            <Box>
                                                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                                                    {residency === 'DIASPORA' ? copy.diasporaGuideTitle : copy.localGuideTitle}
                                                </Typography>
                                                <Typography sx={{ color: '#B7B2D9', fontSize: 14, lineHeight: 1.6 }}>
                                                    {residency === 'DIASPORA' ? copy.diasporaGuideBody : copy.localGuideBody}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Card>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 0.12 }}
                            >
                                <Box
                                    sx={{
                                        mt: 3,
                                        p: { xs: 2.4, md: 3 },
                                        borderRadius: 5,
                                        background: 'rgba(18,10,45,0.72)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        boxShadow: '0 18px 50px rgba(0,0,0,0.22)',
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        spacing={2}
                                        sx={{ mb: 1.2 }}
                                    >
                                        <Box>
                                            <Typography sx={{ fontWeight: 900, fontSize: 20 }}>
                                                {copy.weeklyTitle}
                                            </Typography>
                                            <Typography sx={{ color: '#B7B2D9', fontSize: 13, mt: 0.5 }}>
                                                {copy.weeklyBody}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={copy.weeklyBadge}
                                            sx={{
                                                bgcolor: 'rgba(255,138,0,0.14)',
                                                color: '#FFB347',
                                                border: '1px solid rgba(255,138,0,0.22)',
                                                fontWeight: 800,
                                            }}
                                        />
                                    </Stack>

                                    <Grid container spacing={1.4} sx={{ mt: 0.5 }}>
                                        {merchantsToShow.map((merchant, index) => (
                                            <Grid item xs={6} sm={index === 4 ? 12 : 6} key={`${merchant.name}-${index}`}>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1.2}
                                                    sx={{
                                                        minHeight: 58,
                                                        p: 1.2,
                                                        borderRadius: 3,
                                                        background: 'rgba(255,255,255,0.035)',
                                                        border: '1px solid rgba(255,255,255,0.06)',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 38,
                                                            height: 38,
                                                            borderRadius: '50%',
                                                            display: 'grid',
                                                            placeItems: 'center',
                                                            position: 'relative',
                                                            flexShrink: 0,
                                                            background: 'rgba(91,46,255,0.20)',
                                                            border: '1px solid rgba(255,255,255,0.08)',
                                                        }}
                                                    >
                                                        <StorefrontIcon sx={{ color: '#FF8A00', fontSize: 20 }} />
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: -5,
                                                                right: -5,
                                                                width: 18,
                                                                height: 18,
                                                                borderRadius: '50%',
                                                                bgcolor: '#FF8A00',
                                                                color: '#0B0620',
                                                                display: 'grid',
                                                                placeItems: 'center',
                                                                fontSize: 10,
                                                                fontWeight: 900,
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            sx={{
                                                                color: '#FFFFFF',
                                                                fontWeight: 850,
                                                                fontSize: 13,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {merchant.name}
                                                        </Typography>
                                                        <Typography sx={{ color: '#8F86B8', fontSize: 11, fontWeight: 800 }}>
                                                            {merchant.initials}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Box sx={{ py: { xs: 8, md: 10 } }}>
                <Container maxWidth="lg">
                    <Typography
                        sx={{
                            fontSize: { xs: 26, md: 34 },
                            fontWeight: 800,
                            mb: 6,
                        }}
                    >
                        {copy.sectionTitle}
                    </Typography>

                    <Grid container spacing={4}>
                        {featureCards.map((item) => (
                            <Grid item xs={12} md={6} key={item.title}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        p: 3.5,
                                        borderRadius: 6,
                                        background: 'rgba(18,10,45,0.9)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        boxShadow: '0 16px 32px rgba(0,0,0,0.18)',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 4,
                                            background: 'rgba(255,138,0,0.08)',
                                            display: 'grid',
                                            placeItems: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        {item.icon}
                                    </Box>
                                    <Typography sx={{ fontWeight: 800, mb: 1 }}>{item.title}</Typography>
                                    <Typography sx={{ fontSize: 14, color: '#B7B2D9', lineHeight: 1.7 }}>
                                        {item.text}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box sx={{ py: 10, background: 'rgba(15,10,40,0.95)' }}>
                <Container maxWidth="lg">
                    <Box sx={{ textAlign: 'center', mb: 6, maxWidth: 760, mx: 'auto' }}>
                        <Typography sx={{ fontSize: 28, fontWeight: 900 }}>
                            {copy.bottomTitle}
                        </Typography>
                        <Typography sx={{ fontSize: 15, color: '#B7B2D9', mt: 1.5, lineHeight: 1.8 }}>
                            {copy.bottomBody}
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {bottomCards.map((line) => (
                            <Grid item xs={12} md={4} key={line}>
                                <Card
                                    sx={{
                                        p: 3,
                                        height: '100%',
                                        borderRadius: 5,
                                        background: 'rgba(22,15,54,0.85)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                    }}
                                >
                                    <Typography sx={{ color: '#D8D3F2', lineHeight: 1.8 }}>
                                        {line}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>
        </Box>
    )
}

export default Home
